import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, Terminal, Calendar, Music, Play, Pause, 
  Volume2, VolumeX, ChevronRight, Sparkles, Star,
  Clock, Home as HomeIcon, Image, Lock, Unlock, Code, Cpu, X
} from "lucide-react";

// Import static data
import timelineData from "./data/timeline.json";
import photosData from "./data/photos.json";
import configData from "./data/config.json";

// Secret Code Easter Egg Component
const SecretCodeEasterEgg = () => {
  const [typedCode, setTypedCode] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const SECRET_CODE = 'SCREWED';

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (isUnlocked) return;
      
      const key = e.key.toUpperCase();
      if (key.length === 1 && key.match(/[A-Z]/)) {
        setTypedCode(prev => {
          const newCode = (prev + key).slice(-SECRET_CODE.length);
          if (newCode === SECRET_CODE) {
            setIsUnlocked(true);
            playUnlockSound();
          }
          return newCode;
        });
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [isUnlocked]);

  const playUnlockSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.1, ctx.currentTime + i * 0.12 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.12 + 0.3);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.12);
        osc.stop(ctx.currentTime + i * 0.12 + 0.35);
      });
    } catch (e) {
      console.log("Audio not supported");
    }
  };

  const secretMessage = `
/**
 * @file love.config.ts
 * @author seu_coração
 * @version ∞.0.0
 * @description Core configuration for life happiness module
 */

interface LifePartner {
  name: "Tiago";
  role: "amor da minha vida" | "melhor pessoa do mundo";
  priority: typeof Infinity;
  replaceable: false;
}

const config: LifeConfig = {
  
  // Runtime settings
  happiness: {
    enabled: true,
    source: "você",
    level: Number.MAX_SAFE_INTEGER,
    autoIncrement: true
  },

  // Core dependencies  
  dependencies: {
    "tiago": "^forever.0.0",
    "amor": "latest",
    "carinho": "sempre",
    "risadas": "diárias",
    "abraços": "ilimitados"
  },

  // Feature flags
  features: {
    acordarFeliz: true,
    sentirSaudade: true,
    amarMaisCadaDia: true,
    construirFuturoJuntos: true
  },

  // System messages
  logs: [
    "INFO: Vida antes de você -> null",
    "INFO: Vida depois de você -> completa",
    "WARN: Níveis de amor excedendo limites seguros",
    "SUCCESS: Match perfeito encontrado",
    "SUCCESS: Sistema rodando em modo felicidade máxima"
  ]

};

// Immutable truth
export const VERDADE_ABSOLUTA = "Eu te amo, Tiago.";

// This constant will never change
export const PARA_SEMPRE = true;

/*
 * P.S.: Se você encontrou isso, é porque você é
 * curioso como eu imaginava. E é por isso também
 * que eu te amo. Você sempre quer entender como
 * as coisas funcionam por baixo dos panos.
 * 
 * Mas o amor que eu sinto por você não precisa
 * de debugging. Ele só funciona. Sempre.
 * 
 * Feliz aniversário, meu engenheiro favorito. 💜
 */
`;

  return (
    <AnimatePresence>
      {isUnlocked && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={() => setIsUnlocked(false)}
          data-testid="secret-modal"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, rotateX: -15 }}
            animate={{ scale: 1, opacity: 1, rotateX: 0 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            className="relative max-w-3xl w-full max-h-[85vh] overflow-hidden rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#00ffd5]/20 via-[#bd00ff]/20 to-[#ff0099]/20 blur-xl"></div>
            
            <div className="relative bg-[#0a0a0a] border border-[#00ffd5]/30 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(0,255,213,0.3)]">
              <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10 bg-black/50">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="flex items-center gap-2 text-[#00ffd5]">
                  <Unlock className="w-4 h-4" />
                  <span className="font-mono text-sm">DEVELOPER MODE UNLOCKED</span>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-[#bd00ff]" />
                  <Code className="w-4 h-4 text-[#ff0099]" />
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh] font-mono text-sm">
                <pre className="text-[#00ff9d] whitespace-pre-wrap leading-relaxed">
                  <code>{secretMessage}</code>
                </pre>
              </div>

              <div className="px-6 py-4 border-t border-white/10 bg-black/50 flex items-center justify-between">
                <span className="text-xs text-white/40">
                  Código secreto: <span className="text-[#00ffd5]">SCREWED</span> ✓
                </span>
                <button
                  onClick={() => setIsUnlocked(false)}
                  className="px-4 py-2 rounded-full bg-[#00ffd5]/10 border border-[#00ffd5]/30 text-[#00ffd5] text-sm hover:bg-[#00ffd5]/20 transition-colors"
                  data-testid="close-secret-btn"
                >
                  Fechar
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Terminal Component
const TerminalWindow = ({ onComplete }) => {
  const [lines, setLines] = useState([]);
  const [currentLine, setCurrentLine] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const terminalRef = useRef(null);

  const terminalLines = [
    '> boot sequence initialized',
    '> loading affective-response engine...',
    '> ingesting multimodal memory dataset...',
    '> resolving primary entity signature...',
    '> entity matched: TIAGO',
    '> cross-referencing profile attributes...',
    '> detected domains: engineering, teaching, software, AI, ML, astronomy, music, games',
    '> evaluating behavioral markers...',
    '> markers confirmed: brilliance, humor, care, loyalty, intensity, emotional safety',
    '> running longitudinal impact analysis...',
    '> deviation from baseline detected: significant positive transformation',
    '> routine quality index: sharply elevated',
    '> belonging perception: fully established',
    '> final assessment: irreplaceable subject ♥'
  ];

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);

  useEffect(() => {
    if (currentLine >= terminalLines.length) {
      setTimeout(() => onComplete?.(), 1500);
      return;
    }

    const line = terminalLines[currentLine];
    if (currentChar < line.length) {
      const timeout = setTimeout(() => {
        setLines(prev => {
          const newLines = [...prev];
          newLines[currentLine] = line.slice(0, currentChar + 1);
          return newLines;
        });
        setCurrentChar(prev => prev + 1);
      }, 25);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setCurrentLine(prev => prev + 1);
        setCurrentChar(0);
      }, 200);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLine, currentChar, onComplete]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [lines]);

  return (
    <div className="terminal p-6 h-full min-h-[350px]" data-testid="terminal-window">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-3 h-3 rounded-full bg-red-500"></div>
        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
        <div className="w-3 h-3 rounded-full bg-green-500"></div>
        <span className="ml-3 text-xs text-white/40 uppercase tracking-widest">console.log do coração</span>
      </div>
      <div ref={terminalRef} className="terminal-text text-sm leading-relaxed overflow-y-auto max-h-[280px]">
        {lines.map((line, i) => (
          <div key={i} className="mb-1">{line}</div>
        ))}
        <span className={`inline-block w-2 h-4 bg-green-400 ${showCursor ? 'opacity-100' : 'opacity-0'}`}>█</span>
      </div>
    </div>
  );
};

// Time Counter Component
const TimeCounter = ({ startDate }) => {
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const start = new Date(startDate);
    const updateCounter = () => {
      const now = new Date();
      const diff = now - start;
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTime({ days, hours, minutes, seconds });
    };

    updateCounter();
    const interval = setInterval(updateCounter, 1000);
    return () => clearInterval(interval);
  }, [startDate]);

  return (
    <motion.div 
      className="glass-card p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      data-testid="time-counter"
    >
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-[#00ffd5]" />
        <span className="text-xs uppercase tracking-widest text-white/50">Tempo juntos</span>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <div className="counter-segment">
          <div className="counter-value font-mono">{String(time.days).padStart(3, '0')}</div>
          <div className="counter-label">Dias</div>
        </div>
        <div className="counter-segment">
          <div className="counter-value font-mono">{String(time.hours).padStart(2, '0')}</div>
          <div className="counter-label">Horas</div>
        </div>
        <div className="counter-segment">
          <div className="counter-value font-mono">{String(time.minutes).padStart(2, '0')}</div>
          <div className="counter-label">Min</div>
        </div>
        <div className="counter-segment">
          <div className="counter-value font-mono">{String(time.seconds).padStart(2, '0')}</div>
          <div className="counter-label">Seg</div>
        </div>
      </div>
    </motion.div>
  );
};

// Metric Card Component
const MetricCard = ({ label, value, description, delay = 0 }) => (
  <motion.div 
    className="metric-card"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
    <div className="text-2xl font-bold aurora-text mb-2">{value}</div>
    <div className="text-sm text-white/60 leading-relaxed">{description}</div>
  </motion.div>
);

// Timeline Event Component
const TimelineEvent = ({ event, index }) => (
  <motion.div 
    className="timeline-event"
    initial={{ opacity: 0, x: -30, y: 20 }}
    whileInView={{ opacity: 1, x: 0, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ 
      delay: index * 0.4,
      duration: 0.8,
      ease: "easeOut"
    }}
    data-testid={`timeline-event-${index}`}
  >
    <div className="absolute left-0 top-1">
      <div className="timeline-dot"></div>
    </div>
    <div className="glass-card p-5">
      <div className="text-sm font-bold text-[#00ffd5] uppercase tracking-wider mb-2">
        {event.date}
      </div>
      <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
      <p className="text-white/70 leading-relaxed">{event.description}</p>
    </div>
  </motion.div>
);

// Music Player Component - YouTube Audio with Autoplay
const MusicPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showPlayPrompt, setShowPlayPrompt] = useState(true);
  const playerRef = useRef(null);

  const videoId = "oQDsKAdHFao";

  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    window.onYouTubeIframeAPIReady = () => {
      playerRef.current = new window.YT.Player('yt-player', {
        height: '0',
        width: '0',
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          loop: 1,
          playlist: videoId,
          mute: 0,
        },
        events: {
          onReady: (event) => {
            setIsLoaded(true);
            event.target.playVideo();
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              setShowPlayPrompt(false);
            } else if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
              setIsPlaying(false);
            }
          }
        }
      });
    };

    if (window.YT && window.YT.Player) {
      window.onYouTubeIframeAPIReady();
    }

    const handleFirstInteraction = () => {
      if (playerRef.current && !isPlaying) {
        playerRef.current.playVideo();
      }
      document.removeEventListener('click', handleFirstInteraction);
    };
    document.addEventListener('click', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePlay = () => {
    if (playerRef.current) {
      if (isPlaying) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    }
  };

  return (
    <motion.div 
      className="fixed bottom-20 right-6 z-50"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1 }}
      data-testid="music-player"
    >
      <div id="yt-player" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}></div>
      
      {!isPlaying && isLoaded && showPlayPrompt && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute -top-12 right-0 whitespace-nowrap text-xs text-white/60 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10"
        >
          🎵 Clique para tocar nossa música
        </motion.div>
      )}
      
      <button
        onClick={togglePlay}
        disabled={!isLoaded}
        className={`flex items-center gap-3 px-4 py-3 rounded-full backdrop-blur-md border transition-all group ${
          isPlaying 
            ? 'bg-[#00ffd5]/20 border-[#00ffd5]/50' 
            : 'bg-black/60 border-white/10 hover:border-[#00ffd5]/50 hover:bg-black/80'
        } ${!isLoaded ? 'opacity-50' : ''} ${!isPlaying && isLoaded ? 'animate-pulse' : ''}`}
        data-testid="play-pause-btn"
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
          isPlaying ? 'bg-[#00ffd5]' : 'bg-white/10 group-hover:bg-[#00ffd5]'
        }`}>
          {isPlaying ? (
            <Pause className={`w-5 h-5 ${isPlaying ? 'text-black' : 'text-white group-hover:text-black'}`} />
          ) : (
            <Play className={`w-5 h-5 ml-0.5 ${isPlaying ? 'text-black' : 'text-white group-hover:text-black'}`} />
          )}
        </div>
        <div className="text-left">
          <div className="text-sm font-medium flex items-center gap-2">
            Turning Page
            {isPlaying && (
              <span className="flex gap-0.5">
                {[1,2,3].map(i => (
                  <span key={i} className="w-0.5 bg-[#00ffd5] rounded-full animate-pulse" style={{
                    height: `${8 + Math.random() * 8}px`,
                    animationDelay: `${i * 0.15}s`
                  }}></span>
                ))}
              </span>
            )}
          </div>
          <div className="text-xs text-white/50">Sleeping At Last</div>
        </div>
        <Music className={`w-4 h-4 ${isPlaying ? 'text-[#00ffd5]' : 'text-white/50'}`} />
      </button>
    </motion.div>
  );
};

// Love Letter Component
const LoveLetter = () => {
  const [showReveal, setShowReveal] = useState(false);
  
  const letterText = `Tiago,

você mudou a minha vida de um jeito que eu nem sabia que era possível.

Antes de você, parecia que a felicidade estava sempre mais na frente. Como se a vida boa fosse acontecer um dia, no futuro, quando tudo finalmente se encaixasse. E aí você chegou — e fez o hoje ficar bonito. Fez a rotina ficar gostosa. Fez o comum virar especial. Fez a minha vida ganhar riso, calor, leveza, desejo, parceria, segurança e completude.

Você é uma das pessoas mais maravilhosas que eu já conheci. Lindo, inteligente, carinhoso, divertido, brilhante. Seu jeito de cuidar é raro. Seu jeito de amar é raro. Você se doa por inteiro, ama com intensidade, presta atenção, se importa, acolhe, protege. E eu nunca tinha sido amada assim. Nunca tinha me sentido tão escolhida, tão cuidada, tão segura.

Eu amo o seu sorriso e a marquinha que ele forma. Amo o som da sua gargalhada, porque ela acalma o meu mundo. Amo quando você fala comigo com voz fofa. Amo quando você me chama de coação. Amo quando você diz que eu sou a sua vida. Amo até o seu "fala sério" com essa voz rouquinha que atravessou distância, tempo e tudo o que precisava ser atravessado para a gente chegar aqui.

A verdade é que eu não queria te dar qualquer presente. Queria te dar algo que tentasse falar a sua língua, mesmo eu ainda aprendendo esse universo. Algo que tivesse um pedacinho do seu mundo: tecnologia, lógica, criação. Mas, no fim, toda linha deste projeto chega ao mesmo resultado:

você é o amor da minha vida.

Você tornou os meus dias melhores.
Você me faz querer viver o presente.
Você me faz rir de verdade.
Você me faz sentir inteira.
Você me ajuda a ser uma versão melhor de mim.

E se eu conseguisse transformar tudo o que sinto em código, ainda assim não seria suficiente para representar nem 10% do tamanho do amor, da gratidão e da admiração que eu sinto por você.

Feliz aniversário, meu amor.
Obrigada por existir.
Obrigada por me escolher.
Obrigada por ser exatamente quem você é.

Nesse seu segundo aniversário comigo, agora sendo todinho meu e eu todinha sua, eu só quero que você sinta, em cada detalhe, o quanto é amado.

Com todo o meu amor,
Sua Atthena, sua Jaíne, seu "coação".`;

  return (
    <motion.section 
      className="glass-card p-8 md:p-12 relative overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid="love-letter-section"
    >
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-teal-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
      
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <Heart className="w-6 h-6 text-pink-500" />
          <h2 className="text-3xl md:text-4xl font-serif">Relatório final do modelo</h2>
        </div>
        
        <p className="text-white/60 mb-8 max-w-2xl">
          Após consolidar sinais, memórias, padrões de comportamento, impacto emocional acumulado e estabilidade da conexão observada,
          o sistema atingiu confiança suficiente para emitir a conclusão completa.
        </p>
        
        <div className="love-letter max-w-3xl">{letterText}</div>
        
        <div className="flex flex-wrap gap-4 mt-8">
          <button 
            className="btn-aurora"
            onClick={() => setShowReveal(true)}
            data-testid="show-reveal-btn"
          >
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Ver conclusão confidencial
            </span>
          </button>
        </div>
        
        <AnimatePresence>
          {showReveal && (
            <motion.div 
              className="final-reveal mt-8"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              data-testid="final-reveal"
            >
              <p className="font-semibold mb-4 text-[#00ff9d]">Conclusão confidencial do experimento:</p>
              <p className="text-white/80 leading-relaxed">
                Este protótipo começou como uma tentativa de falar um pouco a sua língua.
                Não para impressionar você tecnicamente, porque isso seria impossível, mas para te mostrar que o seu mundo me importa,
                que eu quero atravessar essa ponte até ele, aprender com você e construir algo bonito nele também.
                E, no fim, toda a análise chegou ao mesmo ponto:
                você não foi só uma grande mudança na minha vida.
                <strong className="text-[#00ffd5]"> Você foi a mudança mais bonita.</strong>
              </p>
              <p className="signature mt-6">Feliz aniversário, meu amor. 💙</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  );
};

// Chips/Tags Component
const AttributeChips = () => {
  const chips = [
    'anime', 'música boa', 'IA & machine learning', 'astronomia', 'jogos',
    'reels grudadinhos', 'séries no sofá', 'voz fofa', '"coação"',
    '"você é a minha vida"', 'fala sério', 'gargalhada calmante',
    'amor intenso', 'parceria verdadeira'
  ];

  return (
    <motion.section 
      className="glass-card p-8"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid="attributes-section"
    >
      <div className="flex items-center gap-3 mb-4">
        <Star className="w-6 h-6 text-[#00ffd5]" />
        <h2 className="text-2xl md:text-3xl font-semibold">Variáveis observadas</h2>
      </div>
      <p className="text-white/60 mb-6 max-w-3xl">
        Durante a modelagem, o sistema identificou um conjunto consistente de atributos associados à entidade principal.
        Entre eles: alta capacidade cognitiva, senso de humor diferenciado, cuidado ativo, presença confiável, intensidade afetiva,
        curiosidade intelectual, repertório cultural compatível e forte habilidade de gerar conforto, admiração e alegria.
      </p>
      <div className="flex flex-wrap gap-3">
        {chips.map((chip, i) => (
          <motion.span 
            key={chip}
            className="chip"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
          >
            {chip}
          </motion.span>
        ))}
      </div>
    </motion.section>
  );
};

// Navigation Component
const Navigation = () => {
  const location = useLocation();
  
  return (
    <nav className="nav-fixed py-4" data-testid="navigation">
      <div className="section-container flex items-center justify-between">
        <Link to="/" className="flex flex-col font-mono">
          <span className="aurora-text font-bold text-lg flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#00ffd5]" />
            T.A.I.A.
          </span>
          <span className="text-[10px] text-white/40 ml-7">Teste Afetivo de Inteligência Artificial</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link 
            to="/" 
            className={`nav-link flex items-center gap-2 ${location.pathname === '/' ? 'active' : ''}`}
            data-testid="nav-home"
          >
            <HomeIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Início</span>
          </Link>
          <Link 
            to="/galeria" 
            className={`nav-link flex items-center gap-2 ${location.pathname === '/galeria' ? 'active' : ''}`}
            data-testid="nav-gallery"
          >
            <Image className="w-4 h-4" />
            <span className="hidden sm:inline">Galeria</span>
          </Link>
        </div>
      </div>
    </nav>
  );
};

// Home Page Component
const HomePage = () => {
  const [terminalComplete, setTerminalComplete] = useState(false);

  const scrollToLetter = () => {
    document.getElementById('declaration')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main className="section-container pt-24 pb-16 space-y-12">
      {/* Hero Section */}
      <motion.section 
        className="glass-card p-8 md:p-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="hero-section"
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-white/60">
            Ambiente de validação • Protótipo experimental • Build 10.04
          </span>
        </div>
        
        <h1 className="text-5xl md:text-7xl font-bold mb-4">
          Sistema <span className="aurora-text">AURORA</span>
        </h1>
        
        <p className="text-lg text-white/60 max-w-3xl mb-8 leading-relaxed">
          Um protótipo experimental desenvolvido para modelar impacto humano em sistemas complexos.
          O objetivo deste teste é avaliar como uma presença específica influencia estabilidade emocional,
          percepção de futuro, sensação de pertencimento, expansão de alegria cotidiana e qualidade geral da experiência de viver.
        </p>

        <div className="hero-grid">
          <TerminalWindow onComplete={() => setTerminalComplete(true)} />
          
          <div className="space-y-4">
            <MetricCard 
              label="Entidade principal"
              value="Tiago"
              description="Engenheiro, professor, desenvolvedor de sistemas, especialista em IA e referência absoluta em transformar complexidade em clareza."
              delay={0.1}
            />
            <MetricCard 
              label="Evento de origem"
              value="Baixa probabilidade"
              description="Dois downloads no mesmo dia, no mesmo jogo, no mesmo servidor. A partir daí, a trajetória de ambos deixou de seguir o curso anterior."
              delay={0.2}
            />
            <MetricCard 
              label="Hipótese preliminar"
              value="Impacto estrutural"
              description="A entidade observada alterou profundamente bem-estar, leveza cotidiana, segurança relacional e sentido de lar."
              delay={0.3}
            />
          </div>
        </div>

        <AnimatePresence>
          {terminalComplete && (
            <motion.div 
              className="flex flex-wrap gap-4 mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <button 
                className="btn-aurora"
                onClick={scrollToLetter}
                data-testid="open-report-btn"
              >
                <span className="flex items-center gap-2">
                  Abrir relatório emocional
                  <ChevronRight className="w-4 h-4" />
                </span>
              </button>
              <Link to="/galeria" className="btn-ghost flex items-center gap-2" data-testid="view-gallery-btn">
                <Image className="w-4 h-4" />
                Ver galeria
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>

      {/* Attributes Section */}
      <AttributeChips />

      {/* Timeline Section */}
      <motion.section 
        className="glass-card p-8"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        data-testid="timeline-section"
      >
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="w-6 h-6 text-[#00ffd5]" />
          <h2 className="text-2xl md:text-3xl font-semibold">Registro de marcos críticos</h2>
        </div>
        <p className="text-white/60 mb-8 max-w-3xl">
          A análise temporal mostra que determinados eventos produziram mudanças irreversíveis no sistema.
          A seguir, os pontos de inflexão mais relevantes identificados durante a observação.
        </p>
        <div className="relative">
          <div className="timeline-line"></div>
          <div className="space-y-6">
            {timelineData.map((event, i) => (
              <TimelineEvent key={event.id} event={event} index={i} />
            ))}
          </div>
        </div>
      </motion.section>

      {/* Love Letter Section */}
      <div id="declaration">
        <LoveLetter />
      </div>

      {/* Time Counter - at the end */}
      <TimeCounter startDate={configData.startDate} />

      {/* Footer with subtle dev hint */}
      <footer className="relative py-12" data-testid="footer">
        <p className="text-center text-white/40 text-sm">
          Projeto T.A.I.A. • versão 10.04 • desenvolvido com amor, coragem e um tantinho de audácia romântica.
        </p>
        
        {/* Subtle easter egg hint - bottom right corner */}
        <div 
          className="absolute bottom-4 right-4 flex items-center gap-1.5 text-[10px] text-white/20 font-mono cursor-default hover:text-white/40 transition-colors"
          data-testid="dev-mode-hint"
          title="hmm..."
        >
          <Lock className="w-3 h-3" />
          <span>try typing something...</span>
        </div>
      </footer>
    </main>
  );
};

// Gallery Page Component
const GalleryPage = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  return (
    <main className="section-container pt-24 pb-16" data-testid="gallery-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Nossa <span className="aurora-text">Galeria</span>
        </h1>
        <p className="text-white/60 max-w-2xl">
          Momentos capturados, memórias eternizadas. Cada foto conta um pedacinho da nossa história.
        </p>
      </motion.div>

      <div className="gallery-grid">
        {photosData.map((photo, i) => (
          <motion.div
            key={photo.id}
            className="gallery-item photo-card cursor-pointer"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.1 }}
            onClick={() => setSelectedPhoto(photo)}
            data-testid={`gallery-photo-${i}`}
          >
            <img src={photo.url} alt="Foto" loading="lazy" />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
            data-testid="lightbox"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative max-w-5xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={selectedPhoto.url} 
                alt="Foto ampliada" 
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="close-lightbox"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <div className="aurora-bg"></div>
      <div className="stars"></div>
      
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/galeria" element={<GalleryPage />} />
        </Routes>
        <MusicPlayer />
        <SecretCodeEasterEgg />
      </BrowserRouter>
    </div>
  );
}

export default App;
